import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeaderIcon from "../../components/PageHeaderIcon";
import PillSearchField from "../../components/PillSearchField";
import StatusPill from "../../components/StatusPill";
import SegmentedStatusFilter from "../../components/SegmentedStatusFilter";
import CountryFilterButton from "../../components/CountryFilterButton";
import PillButtonGroup from "../../components/PillButtonGroup";
import {
  fetchOrders as fetchOrdersAPI,
  fetchOrderCountries,
  fetchOrderDetails,
  markOrderReturned,
} from "../../Services/apiService";
import socketService from "../../Services/socketService";
import { getProgressStatusStyle } from "../../theme/statusColors";
import { downloadAllOrderPDFs } from "../../utils/pdfGenerator";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";

// Progress status hierarchy (from lowest to highest priority)
const PROGRESS_STATUS_HIERARCHY = [
  "unprocessed",
  "dispatched",
  "received by customer",
  "returned to lab",
  "processing",
  "analysis completed",
  "completed",
  "cancelled",
];

const getHighestPriorityStatus = (statusArray) => {
  if (!statusArray || statusArray.length === 0) return "unprocessed";
  const normalizedStatuses = statusArray.map((s) =>
    s ? s.toLowerCase().trim() : "unprocessed"
  );
  let highestPriority = -1;
  let highestStatus = "unprocessed";
  normalizedStatuses.forEach((s) => {
    const priority = PROGRESS_STATUS_HIERARCHY.indexOf(s);
    if (priority > highestPriority) {
      highestPriority = priority;
      highestStatus = s;
    }
  });
  return highestStatus;
};

const getStatusBreakdown = (statusArray, totalTestCount) => {
  if (!statusArray || statusArray.length === 0) {
    return { unprocessed: totalTestCount || 1 };
  }
  const breakdown = {};
  statusArray.forEach((s) => {
    const normalized = s ? s.toLowerCase().trim() : "unprocessed";
    breakdown[normalized] = (breakdown[normalized] || 0) + 1;
  });
  return breakdown;
};

const processOrderPDFs = async (orderData) => {
  try {
    await downloadAllOrderPDFs(orderData);
  } catch (error) {
    console.error("❌ Error processing order PDFs:", error);
    alert(`Error generating PDFs: ${error.message}`);
  }
};

const showShippingLabelWarnings = (orderData, showNotification) => {
  if (!orderData?.data) return 0;

  const itemsWithWarnings = orderData.data.filter(
    (item) => item.shipping_label?.message
  );

  itemsWithWarnings.forEach((item, index) => {
    const message = item.shipping_label.message;
    const orderRef = item.order?.order_number || item.order?.name;
    const text = orderRef ? `${orderRef}: ${message}` : message;

    setTimeout(() => showNotification(text, "warning"), index * 5200);
  });

  return itemsWithWarnings.length;
};

function Orders() {
  const navigate = useNavigate();

  // Mark Returned dialog state
  const [markReturnedDialogOpen, setMarkReturnedDialogOpen] = React.useState(false);
  const [markReturnedTestIds, setMarkReturnedTestIds] = React.useState([]);
  const [markReturnedOrder, setMarkReturnedOrder] = React.useState(null);
  const [selectedTestId, setSelectedTestId] = React.useState("");

  // Filter / search states
  const [status, setStatus] = React.useState("");
  const [progressStatus, setProgressStatus] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [countryOptions, setCountryOptions] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  // Current-page data only — no large in-memory dataset
  const [orders, setOrders] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Pagination
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 30,
  });

  // Increment to force a re-fetch after mutations (process / mark returned)
  const [fetchTrigger, setFetchTrigger] = React.useState(0);

  // Socket / highlight state
  const [socketConnected, setSocketConnected] = React.useState(false);
  const [newOrderIds, setNewOrderIds] = React.useState(new Set());
  const [hoveredRow, setHoveredRow] = React.useState(null);

  // Bulk / single processing state
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [dataGridKey, setDataGridKey] = React.useState(0);
  const [processingBulk, setProcessingBulk] = React.useState(false);
  const [processingSingle, setProcessingSingle] = React.useState(false);

  // Notification state
  const [notification, setNotification] = React.useState({
    show: false,
    message: "",
    type: "success",
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, type = "success") => {
    const severity = type === "warning" ? "warning" : type === "error" ? "error" : "success";
    setNotification({ show: true, message, type });
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 5000);
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ─── Debounce search input (400 ms) ────────────────────────────────────────
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setSelectedRows([]);
      setDataGridKey((k) => k + 1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Reset page when dropdown filters change ───────────────────────────────
  const handleStatusChange = (value) => {
    setStatus(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedRows([]);
    setDataGridKey((k) => k + 1);
  };
  const handleProgressStatusChange = (value) => {
    setProgressStatus(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedRows([]);
    setDataGridKey((k) => k + 1);
  };
  const handleCountryChange = (value) => {
    setCountry(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setSelectedRows([]);
    setDataGridKey((k) => k + 1);
  };

  // ─── Country dropdown options ───────────────────────────────────────────────
  React.useEffect(() => {
    fetchOrderCountries()
      .then((res) => setCountryOptions(res?.countries || []))
      .catch((err) => console.error("❌ Failed to fetch order countries:", err));
  }, []);

  // ─── Main data fetch ────────────────────────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;

    const { page, pageSize } = paginationModel;

    setLoading(true);
    setError(null);

    fetchOrdersAPI(page + 1, pageSize, {
      searchQuery: debouncedSearch,
      fulfillmentStatus: status,
      progressStatus,
      country,
    })
      .then((res) => {
        if (cancelled) return;
        const items = (res?.orders || []).map((o) => ({
          ...o,
          id: o.order_id,
        }));
        const total = res?.total ?? res?.totalCount ?? res?.totalItems ?? 0;
        setOrders(items);
        setTotalCount(total || items.length);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [
    debouncedSearch,
    status,
    progressStatus,
    country,
    paginationModel.page,
    paginationModel.pageSize,
    fetchTrigger,
  ]);

  // Re-fetch current page after a mutation (process / mark returned)
  const refreshOrders = () => setFetchTrigger((t) => t + 1);

  // ─── Socket — prepend new real-time orders to the visible list ─────────────
  const handleNewOrder = React.useCallback((newOrder) => {
    setOrders((prev) => {
      if (prev.some((o) => o.order_id === newOrder.order_id)) return prev;
      const transformed = { ...newOrder, id: newOrder.order_id };
      setNewOrderIds((ids) => new Set([...ids, newOrder.order_id]));
      setTimeout(() => {
        setNewOrderIds((ids) => {
          const s = new Set(ids);
          s.delete(newOrder.order_id);
          return s;
        });
      }, 5000);
      setTotalCount((c) => c + 1);
      return [transformed, ...prev];
    });
  }, []);

  React.useEffect(() => {
    let socket = socketService.connect();
    setSocketConnected(socketService.getStatus());
    if (!socket || !socket.connected) socket = socketService.connect();

    if (socket) {
      socket.on("connect", () => setSocketConnected(true));
      socket.on("disconnect", () => setSocketConnected(false));
      socket.on("connect_error", () => setSocketConnected(false));
    }

    socketService.onNewOrder(handleNewOrder);
    return () => { socketService.offNewOrder(); };
  }, [handleNewOrder]);

  // ─── Progress status options ───────────────────────────────────────────────
  const progressStatusOptions = [
    "unprocessed", "dispatched", "received by customer",
    "returned to lab", "processing", "analysis completed",
    "completed", "cancelled",
  ];

  // ─── Column definitions ────────────────────────────────────────────────────
  const columns = [
    { field: "order_name", headerName: "Order ID", minWidth: 80, flex: 0.5 },
    {
      field: "order_date",
      headerName: "Date",
      minWidth: 90,
      flex: 0.7,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
    },
    { field: "customer_name", headerName: "Name", minWidth: 110, flex: 1 },
    {
      field: "fulfillment_status",
      headerName: "Fulfillment Status",
      minWidth: 100,
      flex: 0.8,
      renderCell: (params) => {
        const s = params.value;
        const isFulfilled = s && s.toLowerCase() === "fulfilled";
        const displayStatus = !s || s === "" ? "Unfulfilled" : s.charAt(0).toUpperCase() + s.slice(1);
        return (
          <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: isFulfilled ? "#17B26A" : "transparent", border: isFulfilled ? "none" : "1.5px solid #000" }} />
                {displayStatus}
              </Box>
            }
            sx={{ backgroundColor: isFulfilled ? "#ECFDF3" : "#fff8dc", color: isFulfilled ? "#067647" : "#000", border: isFulfilled ? "1px solid #ABEFC6" : "1px solid #f0d000", fontSize: "0.75rem", height: "24px", "& .MuiChip-label": { px: 1 } }}
            size="small"
          />
        );
      },
    },
    {
      field: "financial_status",
      headerName: "Payment",
      minWidth: 110,
      flex: 0.9,
      renderCell: (params) => {
        const s = params.value;
        if (!s) return "Unknown";
        const lower = s.toLowerCase();
        const isPaid = lower === "paid";
        const isPending = lower === "pending";
        const isRefunded = lower === "refunded";
        const display = s.charAt(0).toUpperCase() + s.slice(1);
        if (isPaid || isPending || isRefunded) {
          return (
            <Chip
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: isPaid ? "#17B26A" : isRefunded ? "#d9534f" : "transparent", border: isPending ? "1.5px solid #f0d000" : "none" }} />
                  {display}
                </Box>
              }
              sx={{ backgroundColor: isPaid ? "#ECFDF3" : isPending ? "#fff8dc" : "#fdecea", color: isPaid ? "#067647" : isPending ? "#000" : "#d9534f", border: isPaid ? "1px solid #ABEFC6" : isPending ? "1px solid #f0d000" : "1px solid #f5c2c7", fontSize: "0.75rem", height: "24px", "& .MuiChip-label": { px: 1 } }}
              size="small"
            />
          );
        }
        return display;
      },
    },
    {
      field: "progress_status",
      headerName: "Progress Status",
      minWidth: 170,
      flex: 1.3,
      renderCell: (params) => {
        const statusData = params.row.progress_statuses || params.value;
        const testCount = params.row.test_count || 1;
        let displayStatus;
        let statusArray = [];
        if (Array.isArray(statusData)) {
          displayStatus = getHighestPriorityStatus(statusData);
          statusArray = statusData;
        } else {
          displayStatus = statusData || "unprocessed";
          statusArray = [displayStatus];
        }
        const statusBreakdown = getStatusBreakdown(statusArray, testCount);
        const hasMultipleTests = testCount > 1;
        const statusStyle = getProgressStatusStyle(displayStatus);
        const formattedStatus =
          displayStatus === "unprocessed"
            ? "Unprocessed"
            : displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);

        return (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", position: "relative" }}
            onMouseEnter={() => setHoveredRow(params.row.id)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <Chip
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", ...statusStyle.dot }} />
                  {formattedStatus}
                  {hasMultipleTests && (
                    <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.7, fontSize: "0.65rem", fontWeight: "bold" }}>
                      +{testCount - 1}
                    </Typography>
                  )}
                </Box>
              }
              sx={{ ...statusStyle, fontSize: "0.75rem", height: "24px", "& .MuiChip-label": { px: 1 } }}
              size="small"
            />
            {hasMultipleTests && hoveredRow === params.row.id &&
              createPortal(
                <Box sx={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", p: 2, backgroundColor: "white", borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.24)", border: "1px solid #e0e0e0", zIndex: 1300, minWidth: 220, maxWidth: 320 }}>
                  <Typography variant="caption" sx={{ fontWeight: "bold", mb: 1, display: "block", color: "#333" }}>Test Status Breakdown</Typography>
                  {Object.entries(statusBreakdown)
                    .sort((a, b) => PROGRESS_STATUS_HIERARCHY.indexOf(b[0]) - PROGRESS_STATUS_HIERARCHY.indexOf(a[0]))
                    .map(([st, count]) => {
                      const style = getProgressStatusStyle(st);
                      return (
                        <Box key={st} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Chip label={`${count} ${st.charAt(0).toUpperCase() + st.slice(1)}`} size="small" sx={{ ...style, fontSize: "0.7rem", height: "20px", "& .MuiChip-label": { px: 0.5 } }} />
                        </Box>
                      );
                    })}
                </Box>,
                document.body
              )}
          </Box>
        );
      },
    },
    { field: "test_count", headerName: "Test Count", align: "center", headerAlign: "left", minWidth: 90, flex: 0.6 },
    {
      field: "total_price",
      headerName: "Price",
      align: "left",
      headerAlign: "left",
      minWidth: 100,
      flex: 0.7,
      renderCell: (params) => `${params.value} ${params.row.currency}`,
    },
    { field: "shipping_country", headerName: "Country", minWidth: 100, flex: 1.0 },
  ];

  // ─── Bulk processing ───────────────────────────────────────────────────────
  const handleBulkProcess = async () => {
    if (selectedRows.length === 0) {
      showNotification("Please select orders to process", "error");
      return;
    }
    try {
      setProcessingBulk(true);
      const orderIds = selectedRows.map((row) => row.order_id);
      const response = await fetchOrderDetails(orderIds);
      const warningCount = showShippingLabelWarnings(response, showNotification);
      processOrderPDFs(response);
      setSelectedRows([]);
      setDataGridKey((k) => k + 1);
      const successMessage = `Successfully processed ${orderIds.length} orders and generated PDFs`;
      if (warningCount > 0) {
        setTimeout(() => showNotification(successMessage, "success"), warningCount * 5200);
      } else {
        showNotification(successMessage, "success");
      }
    } catch (error) {
      showNotification(`Error processing orders: ${error.message}`, "error");
    } finally {
      setProcessingBulk(false);
      refreshOrders();
    }
  };

  // ─── Excel export (current page) ──────────────────────────────────────────
  const handleExportToExcel = () => {
    const exportData = orders.map((order) => ({
      "Order ID": order.order_name || "",
      Date: order.order_date ? new Date(order.order_date).toLocaleDateString() : "",
      Name: order.customer_name || "",
      "Fulfillment Status": (() => {
        const s = order.fulfillment_status;
        return !s || s === "" ? "Unfulfilled" : s.charAt(0).toUpperCase() + s.slice(1);
      })(),
      Payment: (() => {
        const s = order.financial_status;
        return !s ? "Unknown" : s.charAt(0).toUpperCase() + s.slice(1);
      })(),
      "Progress Status": order.progress_status || "Unprocessed",
      "Test Count": order.test_count || 0,
      Price: order.total_price || "",
      Country: order.shipping_country || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    const colWidths = Object.keys(exportData[0] || {}).map((key) => {
      const maxLen = Math.max(key.length, ...exportData.map((r) => String(r[key] || "").length));
      return { wch: Math.min(maxLen + 2, 30) };
    });
    ws["!cols"] = colWidths;
    XLSX.writeFile(wb, `orders_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Processing overlay */}
      {(processingBulk || processingSingle) && (
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255,255,255,0.8)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {processingBulk ? `Processing ${selectedRows.length} Orders` : "Processing Order"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we process your orders...
          </Typography>
        </Box>
      )}

      {/* Top row: title + live indicator */}
      <Box sx={{ px: 1, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
        <PageHeaderIcon src="/icons/ic_orders.svg" />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>Orders</Typography>

        <StatusPill connected={socketConnected} />

        <Button
          variant="contained"
          onClick={handleExportToExcel}
          disabled={loading || orders.length === 0}
          sx={{ ml: "auto", textTransform: "none", borderRadius: 1.5, px: 2.5, py: 0.5, minHeight: 0, fontSize: "0.8125rem", backgroundColor: "#163666", color: "#fff", "&:hover": { backgroundColor: "#112B51" } }}
        >
          Export
        </Button>
      </Box>

      {/* Search + filters + actions row */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          rowGap: 1.5,
          flexWrap: "nowrap",
          alignItems: "center",
          mx: 1,
          mb: 2,
          p: 1.5,
          backgroundColor: "#FFFFFF",
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <PillSearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Enter order ID or customer name"
            resultCount={totalCount}
            loading={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedStatusFilter
            value={progressStatus}
            onChange={handleProgressStatusChange}
            options={progressStatusOptions.map((opt) => ({ value: opt, label: opt.charAt(0).toUpperCase() + opt.slice(1) }))}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedStatusFilter
            value={status}
            onChange={handleStatusChange}
            options={[{ value: "fulfilled", label: "Fulfilled" }, { value: "unfulfilled", label: "Unfulfilled" }]}
            disabled={loading}
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <CountryFilterButton value={country} onChange={handleCountryChange} options={countryOptions} disabled={loading} />
        </Box>

        <PillButtonGroup sx={{ ml: "auto", flexShrink: 0 }}>
          <Button
            variant="text"
            disabled={selectedRows.length !== 1 || processingBulk || processingSingle}
            sx={{ px: 1.25, whiteSpace: "nowrap" }}
            onClick={() => {
              const order = selectedRows[0];
              if (!order) return;
              let testIds = [];
              if (order.order_number && order.test_count) {
                for (let i = 1; i <= order.test_count; i++) testIds.push(`${order.order_number}-${i}`);
              } else if (order.test_ids && Array.isArray(order.test_ids)) {
                testIds = order.test_ids;
              } else if (order.test_id) {
                testIds = [order.test_id];
              }
              setMarkReturnedOrder(order);
              setMarkReturnedTestIds(testIds);
              setSelectedTestId("");
              setMarkReturnedDialogOpen(true);
            }}
          >
            Mark Returned
          </Button>
          <Button
            variant="text"
            disabled={selectedRows.length === 0 || processingBulk}
            onClick={handleBulkProcess}
            sx={{ px: 1.25, minWidth: 90, whiteSpace: "nowrap" }}
          >
            {processingBulk ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Processing...
              </Box>
            ) : selectedRows.length === 0 ? (
              "Process"
            ) : (
              `Process ${selectedRows.length}`
            )}
          </Button>
        </PillButtonGroup>
      </Box>

      <Dialog open={markReturnedDialogOpen} onClose={() => setMarkReturnedDialogOpen(false)}>
            <DialogTitle>Select Test ID to Mark as Returned</DialogTitle>
            <DialogContent>
              {markReturnedOrder && markReturnedTestIds.length > 0 ? (
                <RadioGroup value={selectedTestId} onChange={(e) => setSelectedTestId(e.target.value)}>
                  {markReturnedTestIds.map((tid) => (
                    <FormControlLabel key={tid} value={tid} control={<Radio />} label={tid} />
                  ))}
                </RadioGroup>
              ) : (
                <Typography>No test IDs found for this order.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMarkReturnedDialogOpen(false)} color="inherit">Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedTestId || processingSingle}
                onClick={async () => {
                  if (!markReturnedOrder || !selectedTestId) return;
                  try {
                    setProcessingSingle(true);
                    await markOrderReturned(markReturnedOrder.order_number, selectedTestId);
                    showNotification(
                      `Order ${markReturnedOrder.order_name || markReturnedOrder.order_id} marked as returned to lab (Test ID: ${selectedTestId})`,
                      "success"
                    );
                  } catch (error) {
                    showNotification(`Error marking order as returned: ${error.message}`, "error");
                  } finally {
                    setMarkReturnedDialogOpen(false);
                    setProcessingSingle(false);
                    refreshOrders();
                  }
                }}
              >
                Continue
              </Button>
            </DialogActions>
          </Dialog>

      {/* Notification bar */}
      {notification.show && (
        <Box sx={{ mx: 1, mb: 2, p: 2, borderRadius: 2, backgroundColor: notification.type === "success" ? "rgba(76,175,80,0.1)" : notification.type === "warning" ? "rgba(255,152,0,0.1)" : "rgba(244,67,54,0.1)", border: `1px solid ${notification.type === "success" ? "rgba(76,175,80,0.3)" : notification.type === "warning" ? "rgba(255,152,0,0.3)" : "rgba(244,67,54,0.3)"}`, color: notification.type === "success" ? "#2e7d32" : notification.type === "warning" ? "#e65100" : "#d32f2f", display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: notification.type === "success" ? "#4caf50" : notification.type === "warning" ? "#ff9800" : "#f44336", flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{notification.message}</Typography>
        </Box>
      )}

      {/* Data table */}
      <Box sx={{ mx: 1, mb: 2, backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 2px rgba(16,24,40,0.04)" }}>
        {error ? (
          <Box sx={{ p: 3, textAlign: "center", minHeight: 400, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>Error loading orders</Typography>
            <Typography color="error" sx={{ mb: 3 }}>{error}</Typography>
            <Button variant="contained" color="primary" onClick={() => setFetchTrigger((t) => t + 1)}>Retry</Button>
          </Box>
        ) : (
          <DataGrid
            key={dataGridKey}
            rows={orders}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={40}
            columnHeaderHeight={40}
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[30, 50, 100]}
            onRowClick={(params) => navigate(`/orders/${params.row.order_id}`)}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              const arr = Array.isArray(newSelection)
                ? newSelection
                : Array.from(newSelection?.ids ?? []);
              setSelectedRows(orders.filter((o) => arr.includes(o.id)));
            }}
            disableColumnSelector
            disableDensitySelector
            getRowClassName={(params) =>
              newOrderIds.has(params.row.order_id) ? "new-order-highlight" : ""
            }
            sx={{
              border: "none",
              borderRadius: 0,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", justifyContent: "center" },
              "& .MuiDataGrid-cell:not([data-field='progress_status'])": { justifyContent: "flex-start" },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none !important", border: "none !important" },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none !important" },
              "& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within": { outline: "none !important" },
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .new-order-highlight": {
                backgroundColor: "rgba(76,175,80,0.1) !important",
                borderLeft: "3px solid #4caf50",
                animation: "fadeInGreen 0.5s ease-in-out, fadeOutGreen 4.5s ease-in-out 0.5s forwards",
                "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(76,175,80,0.2)" },
              },
              "& .new-order-highlight:hover": { backgroundColor: "rgba(76,175,80,0.15) !important" },
              "@keyframes fadeInGreen": { "0%": { backgroundColor: "rgba(76,175,80,0.25)", transform: "scale(1.01)" }, "100%": { backgroundColor: "rgba(76,175,80,0.1)", transform: "scale(1)" } },
              "@keyframes fadeOutGreen": { "0%": { backgroundColor: "rgba(76,175,80,0.1)", borderLeft: "3px solid #4caf50" }, "100%": { backgroundColor: "transparent", borderLeft: "3px solid transparent" } },
            }}
          />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 7 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 420 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Orders;
